import { findFontFamilyById, pickDefaultFontIdForFamily } from '@/lib/fonts/font-loader'
import {
  DEFAULT_FONT_FAMILY_ID,
  DEFAULT_FONT_ID,
  DEFAULT_POINT_SIZE,
} from '@/lib/text/constants/text-defaults'
import type { CaptionDefaultFontInput } from './types/caption-default-font-input'
import type { CaptionFontStyle } from '@/lib/caption/types/caption-font-style'

const FALLBACK_STYLE: CaptionFontStyle = {
  fontFamilyId: DEFAULT_FONT_FAMILY_ID,
  fontId: DEFAULT_FONT_ID,
  pointSize: DEFAULT_POINT_SIZE,
}

/**
 * Resolves the same book/checkpoint-level default text style that
 * the Type panel (`resolvedDefaultFontFamilyId`,
 * `type-panel/hooks/use-default-font-family.ts`) applies to brand-new draft
 * text boxes. Captions aren't created as drafts, so they don't pick up that
 * effect automatically — this lets caption creation resolve the same style
 * up front instead of falling back to the raw system default constants.
 */
export function resolveCaptionDefaultFont({
  bookDefaultFontFamilyId,
  bookDefaultFontSize,
  checkpointDefaultFontName,
  checkpointDefaultFontSize,
  families,
}: CaptionDefaultFontInput): CaptionFontStyle {
  const pointSize = bookDefaultFontSize ?? checkpointDefaultFontSize ?? DEFAULT_POINT_SIZE

  const resolvedFontFamilyId =
    bookDefaultFontFamilyId ??
    (checkpointDefaultFontName
      ? families.find((f) => f.familyName === checkpointDefaultFontName)?.fontFamilyId
      : undefined)

  if (resolvedFontFamilyId == null) {
    return { ...FALLBACK_STYLE, pointSize }
  }

  const fontId = pickDefaultFontIdForFamily(findFontFamilyById(families, resolvedFontFamilyId))

  if (fontId == null) {
    return { ...FALLBACK_STYLE, pointSize }
  }

  return { fontFamilyId: resolvedFontFamilyId, fontId, pointSize }
}
